import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PHONE_COUNTRIES, PhoneCountry } from './phone-countries';

@Component({
  selector: 'app-phone-input',
  imports: [CommonModule, FormsModule],
  templateUrl: './phone-input.html',
  styleUrl: './phone-input.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInput),
      multi: true
    }
  ]
})
export class PhoneInput implements ControlValueAccessor {
  @Input() hasError = false;
  @Input() disabled = false;
  @Input() name = 'phoneNumber';
  @Input() inputId = '';
  @Output() blurred = new EventEmitter<void>();

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  constructor(private host: ElementRef<HTMLElement>) {}

  readonly countries = PHONE_COUNTRIES;
  selectedCountry: PhoneCountry = PHONE_COUNTRIES[0];
  filteredCountries: PhoneCountry[] = PHONE_COUNTRIES;
  dropdownOpen = false;
  search = '';
  value = '';

  private onChange = (_value: string) => {};
  private onTouched = () => {};

  writeValue(value: string): void {
    this.value = this.formatPhoneNumber(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  flagUrl(iso: string): string {
    return 'https://flagcdn.com/w20/' + iso + '.png';
  }

  toggleDropdown(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled) return;
    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen) {
      this.search = '';
      this.filteredCountries = this.countries;
      setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
    }
  }

  closeDropdown(): void {
    this.dropdownOpen = false;
    this.search = '';
    this.filteredCountries = this.countries;
  }

  onSearchChange(raw: string): void {
    this.search = raw;
    const query = (raw || '').toString().trim().toLowerCase();
    if (!query) {
      this.filteredCountries = this.countries;
      return;
    }
    this.filteredCountries = this.countries.filter((country) => {
      return country.name.toLowerCase().includes(query)
        || country.dialCode.replace('+', '').includes(query.replace('+', ''))
        || country.iso.includes(query);
    });
  }

  selectCountry(country: PhoneCountry, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectedCountry = country;
    this.closeDropdown();
  }

  onPhoneInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const formatted = this.formatPhoneNumber(target.value);
    this.value = formatted;
    target.value = formatted;
    this.onChange(formatted);
  }

  onPhoneKeydown(event: KeyboardEvent): void {
    if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1
      || (event.keyCode === 65 && event.ctrlKey)
      || (event.keyCode === 67 && event.ctrlKey)
      || (event.keyCode === 86 && event.ctrlKey)
      || (event.keyCode === 88 && event.ctrlKey)
      || (event.keyCode >= 35 && event.keyCode <= 39)) {
      return;
    }
    if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
      event.preventDefault();
    }
  }

  onBlur(): void {
    this.onTouched();
    this.blurred.emit();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.dropdownOpen) return;
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.closeDropdown();
    }
  }

  private formatPhoneNumber(value: string): string {
    const cleaned = (value || '').replace(/\D/g, '').slice(0, 10);
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }
}
